import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, Typography } from '@mui/material'
import { z } from 'zod'
import { AppForm, FormTextField, AppFormProps, FormCheckbox } from '../../../components/FormCore'
import { useZodForm } from '../../../hooks/zod-form'
import { useGetAdminSuppliers } from '../../../api/supplier'
import { useGetCustomerFromGovAPI } from 'src/api/customer'
import { ISupplier } from '../../../generated-types'
import { GenericSupplierFormFields } from './constants'
import { AppTextField } from '../../../components/AppTextField'
import { GenericDialogLayout } from '../../../components/GenericDialogLayout'
import { MainButton } from '../../../components/MainButton'
import { useEffect, useState } from 'react'
import { APIResultCount } from '../../../constant'
import { Controller } from 'react-hook-form'
interface SupplierTableProps extends AppFormProps {
  isSuperAdmin: boolean
  title?: string
  parentSupplierId?: EntityId
}

export const SupplierForm: React.FC<SupplierTableProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  isSuperAdmin,
  title,
  parentSupplierId,
}) => {
  const { t: translate } = useTranslation(['supplier', 'common'])
  const { data: suppliers } = useGetAdminSuppliers(0, APIResultCount.Max)
  const [parentSupplier, setParentSupplier] = useState<ISupplier | undefined>()
  useEffect(() => {
    if (parentSupplierId) {
      const filteredSuppliers = suppliers?.items.find(supplier => supplier.id == parentSupplierId)
      setParentSupplier(filteredSuppliers)
    }
  }, [parentSupplierId, suppliers])

  const schema = isSuperAdmin
    ? z.object({
        ...GenericSupplierFormFields,
        urlHomepage: z.string().or(z.null()).optional(),
        phone: z.string().or(z.null()).optional(),
        streetAddress: z.string().or(z.null()).optional(),
        postaCode: z.string().or(z.null()).optional(),
        city: z.string().or(z.null()).optional(),
        land: z.string().or(z.null()).optional(),
        contactPerson: z.string().or(z.null()).optional(),
        isMooringAnalysisProvider: z.boolean(),
      })
    : z.object({
        ...GenericSupplierFormFields,
        parentSupplierId: z
          .object({
            id: z.number(),
          })
          .or(z.null())
          .transform(value => (value !== null ? value.id : value))
          .optional(),
        contactPerson: z.string().or(z.null()).optional(),
      })

  const form = useZodForm(schema, {
    defaultValues: {
      ...initialValues,
      contactPersonId: initialValues?.contactPerson,
      parentSupplierId: initialValues?.parentSupplier ?? parentSupplier ? parentSupplier : null,
    },
  })

  useEffect(() => {
    form.reset({
      ...initialValues,
      contactPersonId: initialValues?.contactPerson,
      parentSupplierId: initialValues?.parentSupplier
        ? initialValues?.parentSupplier
        : parentSupplier
        ? parentSupplier
        : null,
    })
  }, [form, initialValues, parentSupplier])

  const isUpdateForm = initialValues?.id

  const displayValue = isSuperAdmin ? 'box' : 'none'
  const [orgNo, setOrgNo] = useState<string | undefined>(undefined)

  const { data: orgData } = useGetCustomerFromGovAPI(orgNo)

  useEffect(() => {
    if (orgData) {
      form.setValue('name', orgData.Name ?? '')
      form.setValue('streetAddress', orgData.Address ?? '')
      form.setValue('postaCode', orgData.PostalCode ?? '')
      form.setValue('city', orgData.City ?? '')
      form.setValue('land', orgData.Country ?? '')
      form.setValue('phone', orgData.Phone ?? '')
      form.setValue('urlHomepage', orgData.Website ?? '')
    }
  }, [orgData])

  const handleInputBlur = async event => {
    const inputValue = event.target.value?.trim()
    setOrgNo(inputValue || undefined)
  }

  return (
    <GenericDialogLayout
      title={title}
      pop={onCancel}
      removedCloseButton={false}
      actions={[
        <MainButton
          key={'submit-btn'}
          onClick={() => {
            form.handleSubmit(onSubmit)()
          }}
        >
          {isUpdateForm ? translate('update', { ns: 'common' }) : translate('add', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <AppForm form={form} onSubmit={onSubmit} hasInitialValues={isUpdateForm}>
        {isUpdateForm && <FormTextField name="id" type="hidden" />}
        <FormTextField
          name="organizationNumber"
          label={translate('organizationNumber', { ns: 'supplier' })}
          onBlur={handleInputBlur}
        />
        <Typography variant="caption" color="textSecondary" sx={{ margin: 1, display: 'block' }}>
          {translate('org-number-auto-fetch-text')}
        </Typography>
        <Controller
          name="name"
          control={form.control}
          render={({ field, fieldState }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              fullWidth
              label={translate('name', { ns: 'common' })}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Box display={!isSuperAdmin ? 'box' : 'none'}>
          <Autocomplete
            options={(suppliers?.items as ISupplier[]) || []}
            getOptionLabel={option => option.name}
            renderOption={(props: any, option) => (
              <Box {...props} key={option.id}>
                {option.name}
              </Box>
            )}
            onChange={(e, value) => {
              form.setValue('parentSupplierId', value as any)
            }}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={(form.getValues('parentSupplierId') as any) ?? null}
            renderInput={params => (
              <AppTextField
                {...params}
                name="parentSupplierId"
                label={translate('globalSupplier', { ns: 'supplier' })}
                inputProps={{
                  ...params.inputProps,
                }}
              />
            )}
          />
        </Box>

        <Controller
          name="urlHomepage"
          control={form.control}
          render={({ field, fieldState }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              fullWidth
              label={translate('urlHomepage', { ns: 'common' })}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="phone"
          control={form.control}
          render={({ field, fieldState }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              fullWidth
              label={translate('phone', { ns: 'common' })}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="streetAddress"
          control={form.control}
          render={({ field, fieldState }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              fullWidth
              label={translate('streetAddress', { ns: 'common' })}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="postaCode"
          control={form.control}
          render={({ field, fieldState }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              fullWidth
              label={translate('postaCode', { ns: 'common' })}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="city"
          control={form.control}
          render={({ field, fieldState }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              fullWidth
              label={translate('city', { ns: 'common' })}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="land"
          control={form.control}
          render={({ field, fieldState }) => (
            <AppTextField
              {...field}
              value={field.value ?? ''}
              fullWidth
              label={translate('land', { ns: 'common' })}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <FormTextField name="notes" label={translate('notes', { ns: 'supplier' })} multiline minRows={4} />
        <FormTextField name="contactPerson" label={translate('contactPerson', { ns: 'supplier' })} />
        {isSuperAdmin && (
          <FormCheckbox
            name="isMooringAnalysisProvider"
            label={translate('isMooringAnalysisProvider', { ns: 'supplier' })}
            defaultValue={initialValues?.isMooringAnalysisProvider ?? false}
          />
        )}
      </AppForm>
    </GenericDialogLayout>
  )
}
